import { getDownloadURL, getMetadata, ref } from "firebase/storage";
import { storage, authReady } from "../config/firebase";

// Helpers for the "take a screenshot" flow. The dashboard sends a `screenshot`
// action to the device; the device captures, uploads to
// `screenshots/{serial}.png` (overwriting the same object), and bumps the
// object's `updated` time. To show the NEW capture we poll Storage until a
// screenshot newer than the one that existed BEFORE we sent the action appears.

const screenshotRef = (serialName) =>
  ref(storage, `screenshots/${serialName}.png`);

const updatedMs = (metadata) =>
  Date.parse(metadata?.updated || metadata?.timeCreated) || 0;

// getDownloadURL() ALREADY returns a URL with a query string
// (…?alt=media&token=<token>). Appending "?t=…" a second time made the token
// value `…<token>?t=…`, which Firebase rejects (403) — so the <img> failed to
// load and showed its dark background ("goes black"). Always append the
// cache-buster with the correct separator.
export const withCacheBust = (url) =>
  `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;

// Upload time (ms) of the screenshot that currently exists, or 0 if none.
// Captured BEFORE sending the action so the poll can detect a strictly newer one.
export const getScreenshotBaseline = async (serialName) => {
  if (!serialName) return 0;
  try {
    await authReady; // Storage rules gate /screenshots/* on auth != null
    const baseline = updatedMs(await getMetadata(screenshotRef(serialName)));
    console.log("[screenshot] baseline for", serialName, "=", baseline);
    return baseline;
  } catch (e) {
    console.log("[screenshot] no existing screenshot for", serialName, e?.code || e);
    return 0;
  }
};

// Poll until a screenshot newer than `baseline` lands (device finished
// uploading), or until timeout. Resolves { url, metadata } or null on timeout.
export const pollForNewScreenshot = async (
  serialName,
  baseline,
  { intervalMs = 2000, timeoutMs = 25000 } = {}
) => {
  if (!serialName) return null;
  const imageRef = screenshotRef(serialName);
  const start = Date.now();
  let attempt = 0;
  while (Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, intervalMs));
    attempt += 1;
    try {
      await authReady;
      const metadata = await getMetadata(imageRef);
      const updated = updatedMs(metadata);
      console.log(`[screenshot] poll #${attempt} updated=${updated} baseline=${baseline} newer=${updated > baseline}`);
      if (updated > baseline) {
        const url = withCacheBust(await getDownloadURL(imageRef));
        console.log("[screenshot] new screenshot ready:", url);
        return { url, metadata };
      }
    } catch (e) {
      console.log(`[screenshot] poll #${attempt} error:`, e?.code || e);
    }
  }
  console.log(`[screenshot] poll timed out after ${attempt} attempts (no newer upload)`);
  return null;
};

// Fetch whatever screenshot currently exists (no waiting). Resolves
// { url, metadata } or null if there isn't one.
export const fetchExistingScreenshot = async (serialName) => {
  if (!serialName) return null;
  try {
    await authReady;
    const imageRef = screenshotRef(serialName);
    const [url, metadata] = await Promise.all([
      getDownloadURL(imageRef),
      getMetadata(imageRef),
    ]);
    return { url: withCacheBust(url), metadata };
  } catch (e) {
    console.log("[screenshot] fetchExisting error:", e?.code || e);
    return null;
  }
};
